import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useAuth } from "./AuthContext";
import { API_URL } from "../config";

const EmailsContext = createContext(null);

export const useEmails = () => {
  const context = useContext(EmailsContext);
  if (context === null) {
    throw new Error("useEmails must be used within an EmailsProvider");
  }
  return context;
};

export const EmailsProvider = ({ children }) => {
  const { user } = useAuth();
  const [showConfigModal, setShowConfigModal] = useState(false);

  // Add WebSocket connection state tracking
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [lastSocketMessage, setLastSocketMessage] = useState(null);
  const [socketDataReceived, setSocketDataReceived] = useState(false);

  const [emails, setEmails] = useState(() => {
    // Initialize from localStorage when context is created
    try {
      const cachedEmails = localStorage.getItem("cached_emails");
      if (cachedEmails) {
        const parsedEmails = JSON.parse(cachedEmails);
        if (
          parsedEmails &&
          (Array.isArray(parsedEmails.needs_response) ||
            Array.isArray(parsedEmails.spam) ||
            Array.isArray(parsedEmails.notify))
        ) {
          console.log("Using cached emails from localStorage on initial load");
          return parsedEmails;
        }
      }
    } catch (error) {
      console.error("Error loading cached emails:", error);
    }

    return {
      needs_response: [],
      spam: [],
      notify: [],
      pagination: {
        total_count: 0,
        total_pages: 0,
        has_more: false,
        category_counts: {
          needs_response: 0,
          spam: 0,
          notify: 0,
        },
      },
    };
  });

  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("needs_response");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewingAllAccounts, setViewingAllAccounts] = useState(
    () => localStorage.getItem("view_all_accounts") === "true"
  );
  const [accountId, setAccountId] = useState(
    () => localStorage.getItem("current_account_id") || null
  );
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const emailsPerPage = 10;

  // WebSocket refs
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const lastHeartbeatRef = useRef(Date.now());
  const heartbeatIntervalRef = useRef(null);

  // Function to check if WebSocket is alive and sending data
  const checkSocketHealth = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const now = Date.now();
      const timeSinceLastMessage = now - lastHeartbeatRef.current;

      // If no message received in last 2 minutes, consider connection stale and reconnect
      if (timeSinceLastMessage > 120000) {
        console.warn(
          "WebSocket appears stale (no messages for 2 minutes), reconnecting..."
        );
        connectWebSocket();
      }
    }
  }, []);

  // WebSocket connection function
  const connectWebSocket = useCallback(() => {
    if (!user?.id || showConfigModal === true) {
      return;
    }

    const token = Cookies.get("access_token");
    if (!token) {
      console.error("No access token available for WebSocket connection");
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Reset socket state
    setIsSocketConnected(false);
    setSocketDataReceived(false);

    const baseUrl =
      import.meta.env.VITE_API_URL_PROD || "https://backend.efficia.ai";
    const wsUrl = `${baseUrl
      .replace("https://", "wss://")
      .replace("http://", "ws://")}/ws/email-fetch/${user.id}/?token=${token}`;
    console.log("Connecting to WebSocket:", wsUrl);

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("WebSocket connected for email fetch");
      setIsSocketConnected(true);
      setError(null);
      lastHeartbeatRef.current = Date.now();

      // Request emails on connection
      ws.send(
        JSON.stringify({
          action: "start_email_fetch",
          page: currentPage,
          category: "all",
        })
      );
    };

    ws.onmessage = (event) => {
      // Update heartbeat timestamp on any message
      lastHeartbeatRef.current = Date.now();
      setLastSocketMessage(Date.now());

      try {
        const message = JSON.parse(event.data);
        console.log("WebSocket message received:", message.type, message);

        // Handle email_notification (if this is used for real-time updates)
        if (message.type === "email_notification" && message.data) {
          console.log(
            "Email notification received via WebSocket:",
            message.data
          );
          setSocketDataReceived(true);
          // Handle email notifications here if needed
          return;
        }

        // Handle different message types
        if (message.type === "fetch_error") {
          console.error("WebSocket fetch error:", message.message);
          setError(message.message || "Email fetch failed");
          setLoading(false);
          return;
        }

        // Handle progress updates
        if (
          message.type === "send_progress_update" ||
          message.type === "fetch_progress"
        ) {
          const progressValue = message.progress || 0;
          console.log("Fetch progress:", progressValue);
          setProgress(progressValue);
          return;
        }

        // Handle real-time new email notifications (keep for future use)
        if (message.type === "new_email" && message.data) {
          console.log("New email received via WebSocket:", message.data);
          setSocketDataReceived(true);

          const newEmail = {
            email: {
              id: message.data.id,
              thread_id: message.data.thread_id,
              subject: message.data.subject,
              body: message.data.body,
              body_text: message.data.body_text,
              draft_text: message.data.draft_text,
              from_name: message.data.from_name,
              from_email: message.data.from_email,
              to_email: message.data.to_email,
              date: message.data.date,
              send_time: message.data.send_time,
              provider: message.data.provider || "gmail",
              is_read: message.data.is_read,
              is_vip: message.data.is_vip,
              is_todo: message.data.is_todo,
              attachments: message.data.attachments,
              deadline: message.data.deadline,
            },
            account: {
              id: message.data.account_id,
              email: message.data.account_email,
              provider: message.data.provider || "gmail",
            },
          };

          const category = message.data.category || "needs_response";

          setEmails((prevEmails) => {
            // Check if email already exists
            const emailExists = prevEmails[category].some(
              (e) => e.email.id === newEmail.email.id
            );

            if (emailExists) {
              console.log("Email already exists, skipping duplicate");
              return prevEmails;
            }

            const updatedEmails = {
              ...prevEmails,
              [category]: [newEmail, ...prevEmails[category]],
              pagination: {
                ...prevEmails.pagination,
                total_count: prevEmails.pagination.total_count + 1,
                category_counts: {
                  ...prevEmails.pagination.category_counts,
                  [category]:
                    prevEmails.pagination.category_counts[category] + 1,
                },
              },
            };

            // Update cache with WebSocket data
            localStorage.setItem(
              "cached_emails",
              JSON.stringify(updatedEmails)
            );
            localStorage.setItem("emails_last_fetch", new Date().toISOString());
            localStorage.setItem("emails_source", "websocket");

            return updatedEmails;
          });

          // Show notification for new email
          if (Notification.permission === "granted") {
            new Notification(`New email from ${message.data.from_name}`, {
              body: message.data.subject,
              icon: "/email-icon.png",
            });
          }

          return;
        }

        // Handle email status updates (keep for future use)
        if (message.type === "email_updated" && message.data) {
          console.log("Email updated via WebSocket:", message.data);
          setSocketDataReceived(true);

          const { email_id, updates } = message.data;

          setEmails((prevEmails) => {
            const updatedEmails = { ...prevEmails };

            ["needs_response", "spam", "notify"].forEach((category) => {
              const emailIndex = updatedEmails[category].findIndex(
                (e) => e.email.id === email_id
              );

              if (emailIndex !== -1) {
                updatedEmails[category][emailIndex] = {
                  ...updatedEmails[category][emailIndex],
                  email: {
                    ...updatedEmails[category][emailIndex].email,
                    ...updates,
                  },
                };
              }
            });

            // Update cache with WebSocket data
            localStorage.setItem(
              "cached_emails",
              JSON.stringify(updatedEmails)
            );
            localStorage.setItem("emails_source", "websocket");

            return updatedEmails;
          });

          return;
        }

        // Handle email deletion notifications (keep for future use)
        if (message.type === "email_deleted" && message.data) {
          console.log("Email deleted via WebSocket:", message.data);
          setSocketDataReceived(true);

          const { email_id } = message.data;

          setEmails((prevEmails) => {
            const updatedEmails = {
              needs_response: prevEmails.needs_response.filter(
                (e) => e.email.id !== email_id
              ),
              spam: prevEmails.spam.filter((e) => e.email.id !== email_id),
              notify: prevEmails.notify.filter((e) => e.email.id !== email_id),
              pagination: {
                ...prevEmails.pagination,
                total_count: prevEmails.pagination.total_count - 1,
                category_counts: {
                  needs_response: prevEmails.needs_response.filter(
                    (e) => e.email.id !== email_id
                  ).length,
                  spam: prevEmails.spam.filter((e) => e.email.id !== email_id)
                    .length,
                  notify: prevEmails.notify.filter(
                    (e) => e.email.id !== email_id
                  ).length,
                },
              },
            };

            // Update cache with WebSocket data
            localStorage.setItem(
              "cached_emails",
              JSON.stringify(updatedEmails)
            );
            localStorage.setItem("emails_source", "websocket");

            return updatedEmails;
          });

          return;
        }

        // Handle fetch complete - This is the main data update
        if (message.type === "fetch_complete") {
          console.log(
            "Processing fetch_complete data from WebSocket:",
            message
          );
          setProgress(100);
          setLoading(false);
          setSocketDataReceived(true);

          // FIXED: Check for data in both 'data' and 'result' fields
          const messageData = message.data || message.result;

          if (!messageData) {
            console.warn("No data found in fetch_complete message");
            setError("No email data received");
            return;
          }

          // Helper function to transform email data to expected format
          const transformEmailData = (
            emailArray,
            accountId,
            accountEmail,
            provider
          ) => {
            if (!Array.isArray(emailArray)) return [];

            return emailArray.map((emailItem) => {
              if (emailItem.email) {
                return emailItem;
              } else {
                return {
                  email: {
                    id: emailItem.id,
                    thread_id: emailItem.thread_id,
                    subject: emailItem.subject || "No Subject",
                    body: emailItem.body || "",
                    body_text: emailItem.body_text || "",
                    draft_text: emailItem.draft_text || "",
                    from_name:
                      emailItem.from_name ||
                      emailItem.from_email?.split("@")[0] ||
                      "Unknown",
                    from_email: emailItem.from_email || "",
                    to_email: emailItem.to_email || "",
                    date: emailItem.date,
                    send_time: emailItem.send_time || emailItem.date,
                    provider: provider || "gmail",
                    is_read: emailItem.is_read || false,
                    is_vip: emailItem.is_vip || false,
                    is_todo: emailItem.is_todo || false,
                    attachments: emailItem.attachments || [],
                    deadline: emailItem.deadline,
                    display_name: emailItem.display_name || emailItem.from_name,
                  },
                  account: {
                    id: accountId,
                    email: accountEmail || "",
                    provider: provider || "gmail",
                  },
                };
              }
            });
          };

          setTimeout(() => {
            setProgress(0);
          }, 1000);

          try {
            const accountEntries = Object.entries(messageData);

            if (accountEntries.length === 0) {
              console.warn("No account data found in fetch_complete message");
              setError("No email data received");
              return;
            }

            // Instead of creating new email structure, merge with existing emails
            setEmails((prevEmails) => {
              let newEmailsToAdd = {
                needs_response: [],
                spam: [],
                notify: [],
              };

              // Process each account's data
              accountEntries.forEach(([accountId, accountData]) => {
                if (accountData && typeof accountData === "object") {
                  console.log(`Processing account ${accountId}:`, accountData);

                  // FIXED: Extract account info properly
                  const accountEmail = accountData.account_email || "";
                  const provider = accountData.provider || "gmail";

                  const needsResponseEmails = transformEmailData(
                    accountData.needs_response || [],
                    accountId,
                    accountEmail,
                    provider
                  );
                  const spamEmails = transformEmailData(
                    accountData.spam || [],
                    accountId,
                    accountEmail,
                    provider
                  );
                  const notifyEmails = transformEmailData(
                    accountData.notify || [],
                    accountId,
                    accountEmail,
                    provider
                  );

                  newEmailsToAdd.needs_response = [
                    ...newEmailsToAdd.needs_response,
                    ...needsResponseEmails,
                  ];
                  newEmailsToAdd.spam = [...newEmailsToAdd.spam, ...spamEmails];
                  newEmailsToAdd.notify = [
                    ...newEmailsToAdd.notify,
                    ...notifyEmails,
                  ];
                }
              });

              // Helper function to merge emails without duplicates
              const mergeEmails = (existingEmails, newEmails) => {
                const existingIds = new Set(
                  existingEmails.map((e) => e.email.id)
                );
                const uniqueNewEmails = newEmails.filter(
                  (e) => !existingIds.has(e.email.id)
                );

                // Sort by date (newest first) and merge
                const combined = [...uniqueNewEmails, ...existingEmails];
                return combined.sort((a, b) => {
                  const dateA = new Date(a.email.send_time || a.email.date);
                  const dateB = new Date(b.email.send_time || b.email.date);
                  return dateB - dateA; // Newest first
                });
              };

              // Check if this is initial load (no existing emails)
              const isInitialLoad =
                prevEmails.needs_response.length === 0 &&
                prevEmails.spam.length === 0 &&
                prevEmails.notify.length === 0;

              let updatedEmails;

              if (isInitialLoad) {
                // For initial load, just set the emails directly
                updatedEmails = {
                  needs_response: newEmailsToAdd.needs_response,
                  spam: newEmailsToAdd.spam,
                  notify: newEmailsToAdd.notify,
                  pagination: {
                    total_count:
                      newEmailsToAdd.needs_response.length +
                      newEmailsToAdd.spam.length +
                      newEmailsToAdd.notify.length,
                    total_pages: Math.ceil(
                      (newEmailsToAdd.needs_response.length +
                        newEmailsToAdd.spam.length +
                        newEmailsToAdd.notify.length) /
                        emailsPerPage
                    ),
                    has_more: false,
                    category_counts: {
                      needs_response: newEmailsToAdd.needs_response.length,
                      spam: newEmailsToAdd.spam.length,
                      notify: newEmailsToAdd.notify.length,
                    },
                  },
                };
                console.log("Initial load - setting emails directly");
              } else {
                // For subsequent updates, merge with existing emails
                const mergedNeedsResponse = mergeEmails(
                  prevEmails.needs_response,
                  newEmailsToAdd.needs_response
                );
                const mergedSpam = mergeEmails(
                  prevEmails.spam,
                  newEmailsToAdd.spam
                );
                const mergedNotify = mergeEmails(
                  prevEmails.notify,
                  newEmailsToAdd.notify
                );

                updatedEmails = {
                  needs_response: mergedNeedsResponse,
                  spam: mergedSpam,
                  notify: mergedNotify,
                  pagination: {
                    total_count:
                      mergedNeedsResponse.length +
                      mergedSpam.length +
                      mergedNotify.length,
                    total_pages: Math.ceil(
                      (mergedNeedsResponse.length +
                        mergedSpam.length +
                        mergedNotify.length) /
                        emailsPerPage
                    ),
                    has_more: false,
                    category_counts: {
                      needs_response: mergedNeedsResponse.length,
                      spam: mergedSpam.length,
                      notify: mergedNotify.length,
                    },
                  },
                };
                console.log("Subsequent update - merging emails");
              }

              console.log("WebSocket data processed successfully:", {
                needs_response: updatedEmails.needs_response.length,
                spam: updatedEmails.spam.length,
                notify: updatedEmails.notify.length,
                new_emails_added: {
                  needs_response: newEmailsToAdd.needs_response.length,
                  spam: newEmailsToAdd.spam.length,
                  notify: newEmailsToAdd.notify.length,
                },
                is_initial_load: isInitialLoad,
              });

              // Validate email structure
              const validateEmailStructure = (emails) => {
                return emails.every((emailData, index) => {
                  const isValid =
                    emailData &&
                    emailData.email &&
                    emailData.email.id &&
                    emailData.account;

                  if (!isValid) {
                    console.error(
                      `Invalid email structure at index ${index}:`,
                      emailData
                    );
                  }
                  return isValid;
                });
              };

              const isValidStructure =
                validateEmailStructure(updatedEmails.needs_response) &&
                validateEmailStructure(updatedEmails.spam) &&
                validateEmailStructure(updatedEmails.notify);

              if (!isValidStructure) {
                console.error(
                  "Invalid email structure detected:",
                  updatedEmails
                );
                setError("Invalid email data structure received");
                return prevEmails; // Return previous emails if structure is invalid
              }

              // Cache with WebSocket source indicator
              localStorage.setItem(
                "cached_emails",
                JSON.stringify(updatedEmails)
              );
              localStorage.setItem(
                "emails_last_fetch",
                new Date().toISOString()
              );
              localStorage.setItem("emails_source", "websocket");

              console.log("WebSocket data processed and cached successfully");
              return updatedEmails;
            });
          } catch (error) {
            console.error(
              "Error processing fetch_complete data from WebSocket:",
              error
            );
            setError("Failed to process email data from WebSocket");
          }

          return;
        }
      } catch (error) {
        console.error("Error processing WebSocket message:", error);
        setError("Failed to process WebSocket message");
        setLoading(false);
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      setError("WebSocket connection error");
      setLoading(false);
      setIsSocketConnected(false);
    };

    ws.onclose = (event) => {
      console.log("WebSocket closed:", event.code, event.reason);
      wsRef.current = null;
      setIsSocketConnected(false);

      // Attempt to reconnect after a delay unless it was a clean close
      if (event.code !== 1000 && user?.id && !showConfigModal) {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log("Attempting to reconnect WebSocket...");
          connectWebSocket();
        }, 3000);
      }
    };
  }, [
    user?.id,
    showConfigModal,
    currentPage,
    emailsPerPage,
    checkSocketHealth,
  ]);

  // Set up heartbeat monitoring
  useEffect(() => {
    if (isSocketConnected) {
      heartbeatIntervalRef.current = setInterval(checkSocketHealth, 15000);
    } else {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    }

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [isSocketConnected, checkSocketHealth]);

  // MAIN fetchEmails function that calls API then connects WebSocket
  const fetchEmails = useCallback(
    async (page = 1, category = "all", forceRefresh = false) => {
      if (!user?.id || showConfigModal === true) {
        console.error(
          "No user ID available for fetching emails or modal not closed yet"
        );
        return null;
      }

      console.log("🚀 fetchEmails called - starting API calls");
      setLoading(true);
      setError(null);

      try {
        // STEP 1: Call API with force_refresh = true
        console.log("📞 API Call 1: force_refresh=true");
        const endpoint = `${API_URL}/email/${user.id}/categories/all`;

        const response1 = await axios.get(endpoint, {
          params: {
            force_refresh: true,
          },
          withCredentials: true,
        });

        console.log("✅ API Response 1:", response1.data);

        // Process and set the emails from API
        let emailsData = {
          needs_response: [],
          spam: [],
          notify: [],
          pagination: {
            total_count: 0,
            total_pages: 0,
            has_more: false,
            category_counts: {
              needs_response: 0,
              spam: 0,
              notify: 0,
            },
          },
        };

        // Handle API response structure - process accounts data
        if (response1.data && response1.data.accounts) {
          console.log(
            "📧 Processing accounts data from API:",
            response1.data.accounts
          );

          // Helper function to transform API email data
          const transformApiEmailData = (
            emailArray,
            accountId,
            accountEmail = "",
            provider = "gmail"
          ) => {
            if (!Array.isArray(emailArray)) return [];

            return emailArray.map((emailItem) => ({
              email: {
                id: emailItem.id,
                thread_id: emailItem.thread_id,
                subject: emailItem.subject || "No Subject",
                body: emailItem.body || "",
                body_text: emailItem.body_text || "",
                body_html: emailItem.body_html || "",
                draft_text: emailItem.draft_text || "",
                from_name: emailItem.from_name || "Unknown",
                from_email: emailItem.from_email || "",
                to_email: emailItem.to_email || "",
                date: emailItem.date,
                send_time: emailItem.send_time || emailItem.date,
                provider: provider,
                is_read: emailItem.is_read || false,
                is_vip: emailItem.is_vip || false,
                is_todo: emailItem.is_todo || false,
                attachments: emailItem.attachments || [],
                deadline: emailItem.deadline,
                display_name:
                  emailItem.from_name ||
                  emailItem.from_email?.split("@")[0] ||
                  "Unknown",
              },
              account: {
                id: accountId,
                email: accountEmail,
                provider: provider,
              },
            }));
          };

          // Process each account's emails
          Object.entries(response1.data.accounts).forEach(
            ([accountId, accountData]) => {
              if (accountData && typeof accountData === "object") {
                console.log(
                  `📧 Processing API account ${accountId}:`,
                  accountData
                );

                const accountEmail = accountData.account_email || "";
                const provider = accountData.provider || "gmail";

                // Transform and add emails from each category
                if (accountData.needs_response) {
                  const transformedEmails = transformApiEmailData(
                    accountData.needs_response,
                    accountId,
                    accountEmail,
                    provider
                  );
                  emailsData.needs_response = [
                    ...emailsData.needs_response,
                    ...transformedEmails,
                  ];
                }

                if (accountData.spam) {
                  const transformedEmails = transformApiEmailData(
                    accountData.spam,
                    accountId,
                    accountEmail,
                    provider
                  );
                  emailsData.spam = [...emailsData.spam, ...transformedEmails];
                }

                if (accountData.notify) {
                  const transformedEmails = transformApiEmailData(
                    accountData.notify,
                    accountId,
                    accountEmail,
                    provider
                  );
                  emailsData.notify = [
                    ...emailsData.notify,
                    ...transformedEmails,
                  ];
                }
              }
            }
          );

          // Sort emails by date (newest first)
          emailsData.needs_response.sort((a, b) => {
            const dateA = new Date(a.email.send_time || a.email.date);
            const dateB = new Date(b.email.send_time || b.email.date);
            return dateB - dateA;
          });

          emailsData.spam.sort((a, b) => {
            const dateA = new Date(a.email.send_time || a.email.date);
            const dateB = new Date(b.email.send_time || b.email.date);
            return dateB - dateA;
          });

          emailsData.notify.sort((a, b) => {
            const dateA = new Date(a.email.send_time || a.email.date);
            const dateB = new Date(b.email.send_time || b.email.date);
            return dateB - dateA;
          });
        }

        // Calculate pagination
        emailsData.pagination = {
          total_count:
            emailsData.needs_response.length +
            emailsData.spam.length +
            emailsData.notify.length,
          total_pages: Math.ceil(
            (emailsData.needs_response.length +
              emailsData.spam.length +
              emailsData.notify.length) /
              emailsPerPage
          ),
          has_more: false,
          category_counts: {
            needs_response: emailsData.needs_response.length,
            spam: emailsData.spam.length,
            notify: emailsData.notify.length,
          },
        };

        // Set emails in state
        setEmails(emailsData);

        // Cache the data
        localStorage.setItem("cached_emails", JSON.stringify(emailsData));
        localStorage.setItem("emails_last_fetch", new Date().toISOString());
        localStorage.setItem("emails_source", "api");

        console.log(
          `✅ Set ${
            emailsData.needs_response.length +
            emailsData.spam.length +
            emailsData.notify.length
          } emails from API`
        );

        // STEP 2: Call API with force_refresh = false after 1 second
        setTimeout(async () => {
          try {
            console.log("📞 API Call 2: force_refresh=false");
            const response2 = await axios.get(endpoint, {
              params: {
                force_refresh: false,
              },
              withCredentials: true,
            });
            console.log("✅ API Response 2:", response2.data);
          } catch (error2) {
            console.error("❌ Error in API call 2:", error2);
          }
        }, 1000);

        setLoading(false);
        return emailsData;
      } catch (error) {
        console.error("❌ Error in API call 1:", error);
        setError("Failed to fetch emails");
        setLoading(false);

        // Fallback to cached data
        try {
          const cachedEmails = localStorage.getItem("cached_emails");
          if (cachedEmails) {
            const parsedEmails = JSON.parse(cachedEmails);
            console.log("📦 Using cached emails as fallback");
            setEmails(parsedEmails);
            return parsedEmails;
          }
        } catch (cacheError) {
          console.error("Error using cached emails:", cacheError);
        }

        return null;
      }
    },
    [user, showConfigModal, emailsPerPage]
  );

  // Call API on mount and then connect WebSocket
  useEffect(() => {
    if (user?.id && !showConfigModal) {
      console.log("🎯 Component mounted - calling fetchEmails");

      // Check for recent cache first
      const cachedEmails = localStorage.getItem("cached_emails");
      const lastFetchTime = localStorage.getItem("emails_last_fetch");

      if (cachedEmails && lastFetchTime) {
        const cacheAge = (new Date() - new Date(lastFetchTime)) / (1000 * 60); // minutes
        if (cacheAge < 5) {
          console.log("📦 Using recent cache, skipping API call");
          const parsedEmails = JSON.parse(cachedEmails);
          setEmails(parsedEmails);
          setInitialFetchDone(true);

          // Still connect WebSocket for live updates
          setTimeout(() => {
            connectWebSocket();
          }, 1000);
          return;
        }
      }

      // Call API then connect WebSocket
      fetchEmails().then(() => {
        console.log("✅ API calls completed, now connecting WebSocket");
        setTimeout(() => {
          connectWebSocket();
        }, 2000);
      });
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [user?.id, showConfigModal, fetchEmails, connectWebSocket]);

  // Modified: Only use cache if no initial fetch done
  useEffect(() => {
    if (user?.id && !initialFetchDone && !socketDataReceived) {
      const cachedEmails = localStorage.getItem("cached_emails");
      const lastFetchTime = localStorage.getItem("emails_last_fetch");

      if (cachedEmails && lastFetchTime) {
        try {
          const now = new Date();
          const lastFetch = new Date(lastFetchTime);
          const cacheAge = (now - lastFetch) / 1000;

          // Only use cache if recent and no socket data yet
          if (cacheAge < 300 && !socketDataReceived && !isSocketConnected) {
            console.log(
              "Using cached emails temporarily until fresh data loads"
            );
            const parsedEmails = JSON.parse(cachedEmails);

            if (
              parsedEmails &&
              (parsedEmails.needs_response?.length > 0 ||
                parsedEmails.spam?.length > 0 ||
                parsedEmails.notify?.length > 0)
            ) {
              setEmails(parsedEmails);
              setInitialFetchDone(true);
              return;
            }
          }
        } catch (error) {
          console.error("Error checking email cache:", error);
        }
      }

      setInitialFetchDone(true);
    }
  }, [user?.id, socketDataReceived, isSocketConnected]);

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setCurrentPage(1);
  };

  const handleAccountChange = useCallback(
    (account) => {
      if (account === null) {
        setAccountId(null);
        setViewingAllAccounts(true);
        localStorage.setItem("view_all_accounts", "true");
        localStorage.removeItem("current_account_id");
      } else {
        setAccountId(account.id);
        setViewingAllAccounts(false);
        localStorage.setItem("view_all_accounts", "false");
        localStorage.setItem("current_account_id", account.id);
      }

      // Clear cached emails since we're changing accounts
      localStorage.removeItem("cached_emails");
      localStorage.removeItem("emails_last_fetch");
      localStorage.removeItem("emails_source");

      setCurrentPage(1);

      // Always fetch fresh data when changing accounts
      setTimeout(() => {
        fetchEmails(1, "all", true);
      }, 100);
    },
    [fetchEmails]
  );

  const deleteEmail = useCallback(
    async (emailId) => {
      try {
        const categories = ["needs_response", "spam", "notify"];
        let emailToDelete = null;

        for (const category of categories) {
          emailToDelete = emails[category]?.find(
            (email) => email.email.id === emailId
          );
          if (emailToDelete) {
            break;
          }
        }

        if (!emailToDelete) {
          throw new Error("Email not found");
        }

        const emailAccountId = emailToDelete.account?.id || accountId;
        let provider = (emailToDelete.email.provider || "gmail").toLowerCase();
        if (provider === "google") {
          provider = "gmail";
        }

        await axios.delete(
          `${API_URL}/email/${user.id}/accounts/${emailAccountId}/emails/${emailId}/delete`,
          {
            params: {
              provider: provider,
            },
            withCredentials: true,
          }
        );

        const updatedEmails = {
          needs_response: emails.needs_response.filter(
            (e) => e.email.id !== emailId
          ),
          spam: emails.spam.filter((e) => e.email.id !== emailId),
          notify: emails.notify.filter((e) => e.email.id !== emailId),
          pagination: {
            ...emails.pagination,
            total_count: emails.pagination.total_count - 1,
            category_counts: {
              needs_response: emails.needs_response.filter(
                (e) => e.email.id !== emailId
              ).length,
              spam: emails.spam.filter((e) => e.email.id !== emailId).length,
              notify: emails.notify.filter((e) => e.email.id !== emailId)
                .length,
            },
          },
        };

        setEmails(updatedEmails);
        localStorage.setItem("cached_emails", JSON.stringify(updatedEmails));

        return true;
      } catch (error) {
        console.error("Failed to delete email", error);
        setError("Failed to delete email");
        return false;
      }
    },
    [user, accountId, emails]
  );

  const value = {
    emails,
    loading,
    error,
    activeCategory,
    currentPage,
    setCurrentPage,
    viewingAllAccounts,
    accountId,
    fetchEmails,
    handleCategoryChange,
    handleAccountChange,
    deleteEmail,
    emailsPerPage,
    showConfigModal,
    setShowConfigModal,
    progress,
    // New values for monitoring WebSocket state
    isSocketConnected,
    lastSocketMessage,
    socketDataReceived,
  };

  return (
    <EmailsContext.Provider value={value}>{children}</EmailsContext.Provider>
  );
};
